import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { XIcon, SearchIcon, UsersIcon, ClockIcon } from "lucide-react";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Friend, UserProfile, addFriend } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";

interface SearchPeopleDialogProps {
  isOpen: boolean;
  onClose: () => void;
  people: UserProfile[];
  sentRequests: Friend[];
  isLoading?: boolean;
  onRefresh?: () => void;
  onAdded?: () => void;
}

export const SearchPeopleDialog: React.FC<SearchPeopleDialogProps> = ({
  isOpen,
  onClose,
  people,
  sentRequests,
  isLoading = false,
  onRefresh,
  onAdded,
}) => {
  const { user } = useAuth();
  const [isMounted, setIsMounted] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [addingId, setAddingId] = useState<string | null>(null);

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setAddingId(null);
    }
  }, [isOpen]);

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return people;
    const q = searchQuery.toLowerCase();
    return people.filter(
      p =>
        (p.name || "").toLowerCase().includes(q) ||
        (p.asal_sekolah || "").toLowerCase().includes(q) ||
        (p.role || "").toLowerCase().includes(q)
    );
  }, [people, searchQuery]);

  const hasPending = (userId: string) =>
    sentRequests.some(r => r.friend_id === userId);

  const getAvatarUrl = (p: UserProfile) => p.avatar_url || "/avatars/ava1.png";

  const handleAdd = async (targetId: string, targetName?: string) => {
    if (!user?.id || addingId || hasPending(targetId)) return;
    setAddingId(targetId);
    try {
      const res = await addFriend(user.id, targetId);
      if (res.success) {
        toast.success(
          `Permintaan pertemanan dikirim ke ${targetName || "pengguna"}`
        );
        onAdded?.();
      } else {
        toast.error(res.error || "Gagal menambahkan teman");
      }
    } catch (e) {
      console.error("Error add friend:", e);
      toast.error("Terjadi kesalahan saat menambahkan teman");
    } finally {
      setAddingId(null);
    }
  };

  const handleRefreshClick = () => {
    setSearchQuery("");
    onRefresh?.();
  };

  if (!isMounted || !isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
      <div className="bg-white rounded-3xl p-6 max-w-md w-full relative shadow-xl transform transition-all duration-300 scale-100 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] rounded-lg flex items-center justify-center">
              <SearchIcon className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-semibold text-[#373e46]">
              Search Friends
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Close search people dialog"
          >
            <XIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Cari berdasarkan nama, sekolah, atau peran"
            className="w-full pl-10 h-12 rounded-xl border-2 border-[#e3e6eb] focus:border-[#6b2cfe] transition-colors"
          />
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto space-y-3 min-h-0">
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div
                  key={i}
                  className="flex items-center gap-3 p-3 animate-pulse"
                >
                  <div className="w-12 h-12 bg-gray-200 rounded-full" />
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-3/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length > 0 ? (
            filtered.map(p => {
              const pending = hasPending(p.id);
              const adding = addingId === p.id;
              return (
                <div
                  key={p.id}
                  className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors duration-200"
                >
                  <Avatar className="w-12 h-12 flex-shrink-0">
                    <AvatarFallback>
                      <img
                        className="w-full h-full object-cover"
                        alt={`${p.name || "User"} Avatar`}
                        src={getAvatarUrl(p)}
                      />
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-[#373e46] text-sm truncate">
                      {p.name}
                    </h4>
                    <p className="text-xs text-gray-500 truncate">
                      {p.asal_sekolah}
                      {p.role ? ` • ${p.role}` : ""}
                    </p>
                  </div>
                  <Button
                    onClick={() => handleAdd(p.id, p.name || undefined)}
                    disabled={pending || adding}
                    className={`px-3 py-2 rounded-xl font-medium transition-all duration-300 text-sm ${pending ? "bg-yellow-100 text-yellow-800 cursor-default" : adding ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] hover:opacity-90 text-white hover:shadow-lg"}`}
                  >
                    {pending ? (
                      <>
                        <ClockIcon className="w-4 h-4" />
                        <span className="ml-1">Pending</span>
                      </>
                    ) : adding ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        <span className="ml-2">Adding...</span>
                      </>
                    ) : (
                      <span>Tambah</span>
                    )}
                  </Button>
                </div>
              );
            })
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <UsersIcon className="w-8 h-8 text-gray-400" />
              </div>
              <h4 className="text-lg font-semibold text-[#373e46] mb-2">
                {searchQuery ? "Tidak ditemukan" : "Tidak ada data"}
              </h4>
              <p className="text-gray-500 text-sm">
                {searchQuery
                  ? `Tidak ada yang cocok dengan "${searchQuery}"`
                  : "Coba segarkan rekomendasi"}
              </p>
              {onRefresh && (
                <Button
                  onClick={handleRefreshClick}
                  className="mt-4 bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] hover:opacity-90 text-white px-4 py-2 rounded-xl font-medium transition-all duration-300 hover:shadow-lg text-sm"
                >
                  Refresh
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
